import { BodyLinkV3 } from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import {
  japaneseStores,
  type StoreChain,
} from '@/app/[locale]/sizing/_data/stores';
import useDisplayBestBuy from '@/app/hooks/useDisplayBestBuy';
import useMatchUserCountry from '@/app/hooks/useMatchUserCountry';

import InStoreSizingOption from './InStoreSizingOption';
import SizingPageSection from './SizingPageSection';

const BestBuyOption = (): JSX.Element => {
  const t = useTranslations();

  return (
    <InStoreSizingOption
      content={t.rich('sizing_bestbuy_store')}
      cta={{
        href: '/best-buy/store-locator',
        label: 'sizing_bestbuy_store_locator',
      }}
      name="best_buy"
    />
  );
};

const JapaneseStoreChainOptionStore = ({
  href,
  name,
  isLast,
}: {
  readonly href: string;
  readonly isLast: boolean;
  readonly name: MessageKey;
}): JSX.Element => {
  const t = useTranslations();

  return (
    <>
      <BodyLinkV3 href={href} rel="noopener noreferrer" target="_blank">
        {t(name)}
      </BodyLinkV3>
      {!isLast && <span>,&nbsp;</span>}
    </>
  );
};

const JapaneseStoreChainOption = ({
  storeName,
  stores,
}: StoreChain): JSX.Element => {
  const content = stores.map((store, i, arr) => (
    <JapaneseStoreChainOptionStore
      href={store.href}
      isLast={arr.length - 1 === i}
      key={store.id}
      name={store.store}
    />
  ));

  return <InStoreSizingOption content={content} name={storeName} />;
};

const InStoreSizingSection = (): JSX.Element => {
  const isBestBuyCountry = useDisplayBestBuy();
  const isJapan = useMatchUserCountry('JP');

  if (!isBestBuyCountry && !isJapan) return <></>;

  return (
    <SizingPageSection
      data-cy="in-store-sizing-section"
      heading={{ content: 'sizing_in_store_now' }}
    >
      {isBestBuyCountry && <BestBuyOption />}
      {isJapan &&
        japaneseStores.map((storeChain) => (
          <JapaneseStoreChainOption key={storeChain.id} {...storeChain} />
        ))}
    </SizingPageSection>
  );
};

export default InStoreSizingSection;
